import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputAdornment, Tooltip,
  Typography,
} from "@deere/fuel-react";
import { DataGrid } from "@mui/x-data-grid";
import { Controller } from "react-hook-form";
import "../assets/Campaigns.scss";
import { useAuthConfig } from "../shared/helper";
import { useOktaAuth } from "@okta/okta-react";
import { Loading } from "../shared/Loading";
import SearchIcon from "@material-ui/icons/Search";
import { CampaignStakeholderModal } from "../shared/modal/CampaignStakeholderModal";
import { UserSearch } from "../shared/UserSearch";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import PropTypes from "prop-types";
import {Block} from "@material-ui/icons";

export const CampaignStakeHolders = ({
  control,
  errors,
  onSelect,
  currentData,
  isError,
  errorMsg,
  onSelectStep,
  campaignId,
  selectedStakeholders,
  setSelectedStakeholders,
  stakeholderList,
  setStakeholderList,
}) => {
  let authAxios = useAuthConfig();
  const { authState } = useOktaAuth();
  const [selectedList, setSelectedList] = useState([]);
  const [isLoadingDataGrid, setIsLoadingDataGrid] = useState(true);
  const [roleList, setRoleList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [query, setQuery] = useState("");
  const [searchParam] = useState(["user", "role", "unit"]);

  const filteredRows = (list) => {
    return list.filter((item) => {
      return searchParam.some((param) => {
        return item[param] &&
          Object.keys(item[param]).length !== 0 &&
          item[param]["description"]
          ? item[param]["description"]
              .toLowerCase()
              .indexOf(query.toLowerCase()) > -1
          : "";
      });
    });
  };

  const handleRowSelection = (data, e) => {
    setSelectedStakeholders(e);
    onSelect(data);
  };

  const handleStakeholderAdd = (obj) => {
    const idObj = { id: Math.floor(Math.random() * 1000000) };
    obj = { ...idObj, ...obj };
    setStakeholderList([obj, ...stakeholderList]);
  };

  const handleSelectedUser = useCallback(
    (value, id) => {
      const updatedRows = stakeholderList.map((row) => {
        if (row.id === id) {
          const descObj = {
            description: value
              ? `${value.lastName},${value.firstName}-${value.racfID}`
              : "",
          };
          row.user = { ...value, ...descObj };
        }
        onSelect(row);
        return row;
      });
      const updateSelectedRows = updatedRows.filter(
        (item) => selectedStakeholders.indexOf(item.id) > -1
      );
      onSelect(updateSelectedRows);
      setStakeholderList(updatedRows);
    },
    [onSelect, selectedStakeholders, setStakeholderList, stakeholderList]
  );

  const handleUnitChange = useCallback(
    (value, id) => {
      const updatedRows = stakeholderList.map((row) => {
        if (row.id === id) {
          const isEmptyVal = { isEmpty: true };
          row.unit = { ...value, ...isEmptyVal };
        }
        return row;
      });
      const updateSelectedRows = updatedRows.filter(
        (item) => selectedStakeholders.indexOf(item.id) > -1
      );
      onSelect(updateSelectedRows);
      setStakeholderList(updatedRows);
    },
    [onSelect, selectedStakeholders, setStakeholderList, stakeholderList]
  );

  const getStakeholdersList = async () => {
    try {
      return await authAxios.get(`/stakeholders/${campaignId}`);
    } catch (e) {
      console.error(e);
    }
  };

  const processStakeHoldersList = (data) => {
    const selectedShList = [];
    if (currentData.stakeholders.length !== 0) {
      for (let item1 of data) {
        for (let item2 of currentData.stakeholders) {
          if (item1.id === item2.id) {
            item1 = item2;
            selectedShList.push(item2);
          }
        }
      }
    } else {
      for (let item of data) {
        if (Object.keys(item.user).length !== 0) {
          selectedShList.push(item);
        }
      }
    }
    setSelectedList(selectedShList);
    setIsLoadingDataGrid(false);
  };

  const getRoles = async () => {
    try {
      return await authAxios.get(
        `/stakeholders?functionalAreaCodes=` +
          currentData?.functionalArea?.description
      );
    } catch (e) {
      console.error(e);
    }
  };

  const getUnits = async () => {
    try {
      return await authAxios.get(`/units`);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    onSelectStep(2);
    getStakeholdersList().then((res) => {
      processStakeHoldersList(res.data);
    });
    getRoles().then((res) => {
      setRoleList(res?.data);
    });

    getUnits().then((res) => {
      setUnitList(res.data);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  const columns = [
    {
      field: "id",
      headerName: "ID",
      hide: true,
    },
    {
      field: "user",
      headerName: "Stakeholder",
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            <UserSearch
              defValue={params.row.user}
              onOptionsChange={handleSelectedUser}
              rowID={params.row.id}
            />
            {(params.row?.user?.racfID && params.row?.userStatus && params.row?.userStatus !== "ACTIVE") ? <Tooltip title={"Invalid User ID"}><Block htmlColor={"red"} /></Tooltip> : null}
          </>
        );
      },
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      renderCell: (params) => {
        let role = params.value.description;
        return (
          <>
            <Typography size="md" component="p">
              {role}
            </Typography>
          </>
        );
      },
    },
    {
      field: "unit",
      headerName: "Unit",
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            {params.value &&
            params.value.description &&
            !params.value.isEmpty ? (
              <Typography display="block" variant="label">
                {params.value.description}
              </Typography>
            ) : (
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  size="small"
                  id="unit-find"
                  options={unitList}
                  defaultValue={params.row.unit.description}
                  getOptionLabel={(option) => {
                    return option?.description ?? option;
                  }}
                  onChange={(e, value) => {
                    handleUnitChange(value, params.row.id);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Unit"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            )}
          </>
        );
      },
    },
  ];

  const rowsData =
    filteredRows(stakeholderList) &&
    filteredRows(stakeholderList).map((item) => {
      return {
        id: item.id,
        user: item.user,
        role: item.role,
        unit: item.unit,
        userStatus: item.user?.userStatus
      };
    });

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box mt={6} mr={6}>
          <Card>
            <CardContent>
              <Box mt={2}>
                <Grid container alignItems="flex-start" spacing={4}>
                  <Grid item xs={3}>
                    <Box ml={4}>
                      <Typography variant="h4">
                        {`(${selectedStakeholders?.length})`} Stakeholders
                        Included
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={9}>
                    {isError && (
                      <Box maxWidth="30rem" pt={0}>
                        <Alert variant={"error"}>{errorMsg}</Alert>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={3}>
                    <CampaignStakeholderModal
                      onSave={handleStakeholderAdd}
                      roles={roleList}
                      units={unitList}
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <FormControl fullWidth>
                      <Input
                        fullWidth
                        placeholder="Search"
                        id="search"
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton aria-label="search">
                              <SearchIcon style={{ color: " #367c2b" }} />
                            </IconButton>
                          </InputAdornment>
                        }
                        onChange={(e) => {
                          setQuery(e.target.value);
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl required fullWidth style={{ height: 375 }}>
                      {isLoadingDataGrid && <Loading />}
                      {!isLoadingDataGrid && (
                        <Controller
                          rules={{ required: true }}
                          control={control}
                          name="selectedStakeholders"
                          defaultValue={selectedList}
                          value={selectedStakeholders}
                          render={({ onChange, value }) => (
                            <DataGrid
                              rows={rowsData}
                              columns={columns}
                              hideFooterPagination
                              checkboxSelection
                              hideFooterSelectedRowCount
                              disableSelectionOnClick
                              disableColumnSelector={true}
                              disableColumnFilter
                              onSelectionModelChange={(ids) => {
                                const selectedIDs = new Set(ids);
                                const selectedRowData = rowsData.filter((row) =>
                                  selectedIDs.has(row.id)
                                );
                                onChange(selectedRowData, ids);
                                handleRowSelection(selectedRowData, ids);
                              }}
                              selectionModel={(() => {
                                return selectedStakeholders;
                              })()}
                            />
                          )}
                        />
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Grid>
    </Grid>
  );
};
CampaignStakeHolders.propTypes = {
  campaignId: PropTypes.any,
  control: PropTypes.any,
  currentData: PropTypes.shape({
    stakeholders: PropTypes.array,
  }),
  errorMsg: PropTypes.any,
  errors: PropTypes.any,
  isError: PropTypes.any,
  onSelect: PropTypes.func,
  onSelectStep: PropTypes.func,
  selectedStakeholders: PropTypes.any,
  setSelectedStakeholders: PropTypes.func,
  stakeholderList: PropTypes.any,
  setStakeholderList: PropTypes.func,
};
